/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * TenantDataCard — Data management card on the Organization detail page
 * ============================================================================
 *
 * Shows the 5 most recent tenant data jobs (backup / purge / restore) of the
 * organization, a quick "Backup now" action, and deep links into
 * /superadmin/tenant-data with the org pre-selected and a tab opened.
 *
 * Error rule: a 409 is already toasted by the API client interceptor, so it
 * is rendered inline only. Every other error is toasted.
 * ============================================================================
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArchiveRestore, DatabaseBackup, History, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ProgressBar } from '@/components/ui';
import { useNotification } from '@/hooks/useNotification';
import { useStartBackup, useTenantDataJobs } from '@/hooks/api/useTenantData';
import { isTerminalStatus, type TenantDataJob } from '@/types/tenant-data.types';
import JobStatusBadge from '@/pages/superadmin/tenant-data/JobStatusBadge';
import {
  getTenantDataErrorMessage,
  shouldToastTenantDataError,
} from '@/pages/superadmin/tenant-data/tenantDataErrors';
import { tenantDataStyles } from '@/pages/superadmin/tenant-data/tenantDataStyles';

const RECENT_JOBS_SIZE = 5;
const TENANT_DATA_ROUTE = '/superadmin/tenant-data';

function progressPercent(job: TenantDataJob): number {
  const progress = job.progress;
  if (!progress || !progress.tablesTotal) return 0;
  return Math.min(100, Math.round((progress.tablesDone / progress.tablesTotal) * 100));
}

function formatDate(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

export default function TenantDataCard({ orgId, orgName }: { orgId: string; orgName: string }) {
  const { t } = useTranslation('platform');
  const { success, error: showError } = useNotification();
  const [inlineError, setInlineError] = useState<string | null>(null);

  const { data, isLoading } = useTenantDataJobs({ orgId, page: 0, size: RECENT_JOBS_SIZE });
  const startBackup = useStartBackup();

  const jobs: TenantDataJob[] = data?.content ?? [];
  const hasActiveJob = jobs.some((job) => !isTerminalStatus(job.status));

  const linkFor = (tab: 'purge' | 'restore' | 'history') =>
    `${TENANT_DATA_ROUTE}?orgId=${encodeURIComponent(orgId)}&tab=${tab}`;

  const handleBackup = () => {
    setInlineError(null);
    startBackup.mutate(orgId, {
      onSuccess: () => {
        success(t('tenantData.backup.started'));
      },
      onError: (err) => {
        const message = getTenantDataErrorMessage(err, t);
        if (shouldToastTenantDataError(err)) {
          showError(message);
        } else {
          setInlineError(message);
        }
      },
    });
  };

  return (
    <section className="tdc-card" aria-labelledby="tdc-title">
      <div className="tdc-header">
        <div className="tdc-header-icon" aria-hidden="true">
          <ArchiveRestore size={20} />
        </div>
        <div className="tdc-header-text">
          <h2 id="tdc-title" className="tdc-title">{t('tenantData.card.title')}</h2>
          <p className="tdc-description">{t('tenantData.card.description', { name: orgName })}</p>
        </div>
        <Button
          variant="primary"
          size="sm"
          className="tdc-backup-btn"
          leftIcon={<DatabaseBackup size={16} />}
          loading={startBackup.isPending}
          disabled={hasActiveJob}
          onClick={handleBackup}
        >
          {t('tenantData.card.backupNow')}
        </Button>
      </div>

      {inlineError && (
        <div className="tdc-banner" role="alert">
          {inlineError}
        </div>
      )}

      <h3 className="tdc-subtitle">{t('tenantData.card.recentJobs')}</h3>

      {isLoading ? (
        <div className="tdc-skeleton" aria-busy="true">
          <div className="tdc-skeleton-row" />
          <div className="tdc-skeleton-row" />
        </div>
      ) : jobs.length === 0 ? (
        <p className="tdc-empty">{t('tenantData.card.noJobs')}</p>
      ) : (
        <ul className="tdc-jobs">
          {jobs.map((job) => {
            const running = !isTerminalStatus(job.status);
            const pct = progressPercent(job);
            return (
              <li key={job.id} className="tdc-job">
                <div className="tdc-job-main">
                  <span className="tdc-job-type">{t(`tenantData.type.${job.type}`)}</span>
                  <JobStatusBadge status={job.status} />
                  <span className="tdc-job-date">{formatDate(job.createdAt)}</span>
                </div>
                {running && (
                  <div
                    className="tdc-job-progress"
                    role="progressbar"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={t('tenantData.progress.title')}
                  >
                    <ProgressBar value={pct} showPercent height={6} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <nav className="tdc-links" aria-label={t('tenantData.card.title')}>
        <Link className="tdc-link tdc-link--danger" to={linkFor('purge')}>
          <Trash2 size={16} aria-hidden="true" />
          <span>{t('tenantData.card.purge')}</span>
        </Link>
        <Link className="tdc-link" to={linkFor('restore')}>
          <ArchiveRestore size={16} aria-hidden="true" />
          <span>{t('tenantData.card.restore')}</span>
        </Link>
        <Link className="tdc-link" to={linkFor('history')}>
          <History size={16} aria-hidden="true" />
          <span>{t('tenantData.card.history')}</span>
        </Link>
      </nav>

      <style>{tenantDataStyles}</style>
      <style>{tdcStyles}</style>
    </section>
  );
}

const tdcStyles = `
  .tdc-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-xl);
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  .tdc-header {
    display: flex;
    align-items: flex-start;
    gap: 0.875rem;
  }
  .tdc-header-icon {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: var(--radius-lg);
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-bg-tertiary);
    color: var(--color-brand-primary);
  }
  .tdc-header-text {
    flex: 1;
    min-width: 0;
  }
  .tdc-title {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--color-text-primary);
  }
  .tdc-description {
    margin: 0.25rem 0 0;
    font-size: 0.875rem;
    color: var(--color-text-secondary);
  }
  .tdc-backup-btn {
    flex-shrink: 0;
  }
  .tdc-banner {
    padding: 0.75rem 1rem;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-warning);
    background: var(--color-warning-light);
    color: var(--color-text-primary);
    font-size: 0.875rem;
  }
  .tdc-subtitle {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--color-text-muted);
  }
  .tdc-empty {
    margin: 0;
    padding: 1rem 0;
    text-align: center;
    font-size: 0.875rem;
    color: var(--color-text-muted);
  }
  .tdc-jobs {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .tdc-job {
    padding: 0.625rem 0.75rem;
    border-radius: var(--radius-md);
    background: var(--color-bg-tertiary);
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    transition: background 0.2s ease;
  }
  .tdc-job:hover {
    background: var(--color-bg-hover);
  }
  .tdc-job-main {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  .tdc-job-type {
    font-weight: 600;
    font-size: 0.875rem;
    color: var(--color-text-primary);
  }
  .tdc-job-date {
    margin-left: auto;
    font-size: 0.75rem;
    color: var(--color-text-muted);
    font-family: var(--font-mono);
  }
  .tdc-job-progress {
    width: 100%;
  }
  .tdc-skeleton {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .tdc-skeleton-row {
    height: 40px;
    border-radius: var(--radius-md);
    background: var(--color-bg-tertiary);
    animation: tdc-pulse 1.4s ease-in-out infinite;
  }
  @keyframes tdc-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  .tdc-links {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--color-border-subtle);
  }
  .tdc-link {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 0.875rem;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border-default);
    color: var(--color-text-link);
    font-size: 0.875rem;
    font-weight: 500;
    text-decoration: none;
    transition: background 0.2s ease, color 0.2s ease;
  }
  .tdc-link:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-link-hover);
  }
  .tdc-link:focus-visible {
    outline: 2px solid var(--color-brand-primary);
    outline-offset: 2px;
  }
  .tdc-link--danger {
    color: var(--color-error);
    border-color: var(--color-border-error);
  }
  .tdc-link--danger:hover {
    color: var(--color-error);
    background: var(--color-error-light);
  }
  @media (max-width: 640px) {
    .tdc-card {
      padding: 1rem;
    }
    .tdc-header {
      flex-wrap: wrap;
    }
    .tdc-backup-btn {
      width: 100%;
    }
    .tdc-job-date {
      margin-left: 0;
      width: 100%;
    }
    .tdc-links {
      flex-direction: column;
    }
    .tdc-link {
      justify-content: center;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .tdc-card *,
    .tdc-card *::before,
    .tdc-card *::after {
      animation: none !important;
      transition: none !important;
    }
  }
`;
